import React from "react";

const AppointmentButtonContainer = ({ appointment, onButtonClick }) => {
  // Define button rules based on conditions
  const buttonRules = [
    {
      label: "Cancel",
      action: "cancel",
      condition: () =>
        !appointment.status.some((status) =>
          [
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
            "No Show",
            "No Show - No Fee",
            "No Show - Charge Fee",
            "Treated",
            "Invoiced",
          ].includes(status)
        ),
    },
    {
      label: "No Show",
      action: "noShow",
      condition: () =>
        !appointment.status.some((status) =>
          [
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
            "No Show",
            "No Show - No Fee",
            "No Show - Charge Fee",
            "Treated",
            "Invoiced",
          ].includes(status)
        ),
    },
    {
      label: "Modify",
      action: "modifyAppointment",
      condition: () =>
        !appointment.status.some((status) =>
          [
            "Completed",
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
            "No Show",
            "No Show - No Fee",
            "No Show - Charge Fee",
            "Treated",
            "Invoiced",
          ].includes(status)
        ),
    },
    {
      label: "Reschedule",
      action: "rescheduleAppointment",
      condition: () =>
        appointment.status.some((status) =>
          ["Changed", "Booked", "Tentative", "Confirmed"].includes(status)
        ) &&
        !appointment.status.some((status) =>
          [
            "Treated",
            "Invoiced",
            "Invoice Paid",
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
            "No Show",
            "No Show - No Fee",
            "No Show - Charge Fee",
            "Completed",
          ].includes(status)
        ),
    },
    {
      label: "Add to Block",
      action: "addToBlock",
      condition: () => !appointment.blockId, // Only show if blockId does NOT exist
    },
    {
      label: "SOAP Notes",
      action: "createSOAPNote",
      condition: () =>
        appointment.history.some((entry) => entry.status === "Treated"), // History is the source of truth
      onClick: (action) => {
        const currentDate = new Date();
        const appointmentStart = new Date(appointment.start);

        if (currentDate < appointmentStart) {
          const formattedStartDate = `${appointmentStart.toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          )}, ${appointmentStart.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          })}`;

          const proceed = window.confirm(
            `Warning: Appointment start date of ${formattedStartDate} has not been reached yet. Are you sure you want to continue?`
          );

          if (!proceed) return;
        }

        onButtonClick(action);
      },
    },
    {
      label: "Revert Treated",
      action: "revertTreated",
      condition: () =>
        appointment.history.some((entry) => entry.status === "Treated") &&
        !appointment.history.some((entry) => entry.status === "Invoiced"), // History-based is more reliable
    },
  ];

  // Status transitions for Phase 1 buttons
  const statusTransitions = {
    Booked: ["Tentative"],
    Tentative: ["Confirmed"],
    Confirmed: ["Treated"],
    Carryover: ["Tentative"],
    Treated: [],
    Cancelled: ["Tentative", "Confirmed"],
    NoShow: [],
    Changed: ["Tentative"],
    Completed: [],
  };

  // Determine all possible next statuses based on the current statuses in the array
  const nextStatuses = [
    ...new Set(
      appointment.status.flatMap((status) => statusTransitions[status] || [])
    ),
  ].filter((status) => !appointment.status.includes(status)); // Filter out statuses that are already present

  const buttonStyles = {
    cancel:
      "border border-red-500 bg-red-50 hover:bg-red-100 text-red-600 shadow-sm",
    noShow:
      "border border-red-500 bg-red-50 hover:bg-red-100 text-red-600 shadow-sm",
    modifyAppointment:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
    manageResources:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
    rescheduleAppointment:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
    modifyBlock:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
    addToBlock:
      "border border-emerald-500 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 shadow-sm",
    createSOAPNote:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
    revertTreated:
      "border border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-600 shadow-sm",
    status:
      "border border-indigo-500 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 shadow-sm",
  };

  return (
    <div className="flex flex-col space-y-4">
      {/* Main Buttons */}
      <div className="flex flex-wrap gap-2">
        {buttonRules.map(
          ({ label, action, condition, onClick }, index) =>
            condition() && (
              <button
                key={index}
                className={`inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background h-9 px-4 py-2 ${buttonStyles[action]}`}
                onClick={() =>
                  onClick ? onClick(action) : onButtonClick(action)
                }>
                {label}
              </button>
            )
        )}
      </div>

      {/* Status Transition Buttons */}
      {nextStatuses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {nextStatuses.map((status, index) => (
            <button
              key={index}
              className={`inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background h-9 px-4 py-2 ${buttonStyles.status}`}
              onClick={() => onButtonClick(`status-${status}`)}>
              Mark as: {status}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentButtonContainer;
